import {
  useDeleteZyusetuValueTemplateMutation,
  ZyusetuFieldKeyFragmentFragment,
  ZyusetuFieldKeyValueTemplateFragmentFragment
} from "@/graphql/graphql";
import {
  Button,
  Card,
//  CardActions,
  CardContent,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Grid2,
  List,
  ListItem,
  ListItemText,
  Stack,
  TextField,
  Typography,
  Box,
} from "@mui/material";
import {useCallback, useState, useMemo} from "react";
import {FileWarning, Plus} from "lucide-react";
import {FieldValueType} from "@/graphql/config";
import {useGetFormattedValue} from "@/hooks/use-get-formatted-value";
import {CreateValueTemplateDialog} from "@/components/dialog/CreateValueTemplateDialog.tsx";
import {EditValueTemplateDialog} from "@/components/dialog/EditValueTemplateDialog.tsx";
import toast from "react-hot-toast";
import {useConfirmDialog} from "@/hooks/use-confirm-dialog.ts";
import {ZyusetuEditType} from "@/types/ZyusetuEditType.ts";
import {useAtom} from "jotai";
import {ZyusetuAtom} from "@/plugin/jotai-wrapper";

interface ZyusetuTemplateSelectorProps {
  fieldKey: ZyusetuFieldKeyFragmentFragment;
  onSelect: (value?: FieldValueType | null, templateDescription?: string | null) => void;
  open: boolean;
  onClose: () => void;
  showCreateDialog?: boolean;
  editType?: ZyusetuEditType
}

export function ZyusetuValueTemplateSelectorDialog({
                                                     fieldKey,
                                                     onSelect, open, onClose, showCreateDialog,
                                                     editType
                                                   }: ZyusetuTemplateSelectorProps) {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedTemplate, setSelectedTemplate] = useState<
    ZyusetuFieldKeyValueTemplateFragmentFragment | null
  >(null);
  
  const filteredTemplates = useMemo(() => {
    return fieldKey.valueTemplate.filter((template) =>
      (template.description?.toLowerCase() ?? '').includes(searchQuery.toLowerCase()) ||
      String(template.value).toLowerCase().includes(searchQuery.toLowerCase())
    );
  }, [fieldKey.valueTemplate, searchQuery]);
  const [_, setZyusetu] = useAtom(ZyusetuAtom);
  const getFormattedValue = useGetFormattedValue();
  
  const [openCreateDialog, setOpenCreateDialog] = useState(showCreateDialog ?? false);
  const [editTemplate, setEditTemplate] = useState<ZyusetuFieldKeyValueTemplateFragmentFragment | null>(null);
  const confirmDialog = useConfirmDialog()
  const deleteTemplate = useDeleteZyusetuValueTemplateMutation()
  
  const onDelete = useCallback(async (id: string) => {
    
    confirmDialog.open({
      fullWidth: true,
      icon: <FileWarning color={"error"}/>,
      title: (
        <Typography
          sx={{color: "primary.contrastText"}}
          fontWeight={"bold"}
        >
          削除してもよろしいですか?この動作は取り消せません
        </Typography>
      ),
      children: (
        <Stack direction={"row"} spacing={2} minWidth={"250px"}>
          <Button
            fullWidth
            variant={'outlined'}
            onClick={async (_) => confirmDialog.close()}
          >
            キャンセル
          </Button>
          <Button
            fullWidth
            variant={'contained'}
            onClick={async () => {
              const toastId = toast.loading('削除中...')
              await deleteTemplate.mutateAsync({
                id
              }).then(
                () => {
                  toast.success('削除しました', {id: toastId})
                  setZyusetu((prev) => {
                    if (!prev) return prev;
                    return {
                      ...prev,
                      template: {
                        ...prev.template,
                        sections: prev.template.sections.map(section => ({
                          ...section,
                          keys: section.keys.map(key => ({
                            ...key,
                            valueTemplate: key.id === fieldKey.id ? key.valueTemplate.filter(template => template.id !== id) : key.valueTemplate
                          }))
                        }))
                      }
                    }
                  })
                  setSelectedTemplate(null);
                }
              ).catch(
                () => {
                  toast.error('削除に失敗しました', {id: toastId})
                }
              )
              confirmDialog.close();
            }}
          >削除する</Button>
        </Stack>
      ),
    });
  }, [confirmDialog, deleteTemplate, fieldKey.id, setZyusetu])
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={"md"}
      fullWidth
    >
      <DialogTitle>
        <Box sx={{display: 'flex', alignItems: 'center', gap: 2}}>
          <Typography variant="h6">テンプレートを選択</Typography>
          <TextField
            size="small"
            placeholder="テンプレートを検索"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            sx={{minWidth: '200px'}}
          />
        </Box>
      </DialogTitle>
      <DialogContent
        sx={{
          p: 3,
          pb: 0,
          display: 'flex',
          flexDirection: 'column',
          height: '600px',
          overflow: 'hidden'
        }}
      >
        {editTemplate && <EditValueTemplateDialog
          open={!!editTemplate}
          onClose={() => setEditTemplate(null)}
          templateId={editTemplate.id}
          currentValue={editTemplate}
          onEditSuccess={async (value: ZyusetuFieldKeyValueTemplateFragmentFragment) => {
            setSelectedTemplate(value);
            setZyusetu((prev) => {
              prev.template = {
                ...prev.template,
                sections: prev.template.sections.map(section => ({
                  ...section,
                  keys: section.keys.map(key => ({
                    ...key,
                    valueTemplate: key.id === fieldKey.id ? key.valueTemplate.map((template) => ({
                      ...template,
                      description: template.id === value.id ? value.description : template.description,
                      value: template.id === value.id ? value.value : template.value
                    })) : key.valueTemplate
                  }))
                }))
              }
            })
          }}
        />
        }
        {openCreateDialog && <CreateValueTemplateDialog
          open={openCreateDialog}
          onClose={() => setOpenCreateDialog(false)}
          currentKeyId={fieldKey.id}
          
          onCreateSuccess={async (value) => {
            setZyusetu((prev) => {
              prev.template = {
                ...prev.template,
                sections: prev.template.sections.map(section => ({
                  ...section,
                  keys: section.keys.map(key => ({
                    ...key,
                    valueTemplate: key.id === fieldKey.id ? [...key.valueTemplate].concat({
                      id: value.id,
                      description: value.description,
                      value: value.value
                    }) : key.valueTemplate
                  }))
                }))
              }
              return prev
            })
            setSelectedTemplate(value);
            onSelect(value.value, value.description);
          }}
        />
        }
        <Box sx={{flexGrow: 1, overflow: 'hidden'}}>
          <Grid2 container spacing={1} sx={{height: "100%", overflow: "hidden"}}>
            <Grid2 size={5} sx={{height: "100%"}}>
              <List sx={{
                width: "100%",
                height: "98%",
                overflowY: "auto",
                "&::-webkit-scrollbar": {
                  width: "4px"
                },
                "&::-webkit-scrollbar-track": {
                  background: "transparent"
                },
                "&::-webkit-scrollbar-thumb": {
                  background: "rgba(0, 0, 0, 0.1)",
                  borderRadius: "2px",
                  "&:hover": {
                    background: "rgba(0, 0, 0, 0.2)"
                  }
                },
                scrollbarWidth: "thin",
                scrollbarColor: "rgba(0, 0, 0, 0.1) transparent"
              }}>
                <Divider component="li"/>
                {filteredTemplates.map((template) => (
                  <>
                    <ListItem
                      alignItems="flex-start"
                      sx={{
                        cursor: "pointer",
                        height: "80px",
                        "&:hover": {
                          backgroundColor: "#f5f5f5",
                        },
                      }}
                      onClick={() => setSelectedTemplate(template)}
                    >
                      <ListItemText
                        primary={
                          <Typography sx={{mb: 1}} variant={'formLabel'}>
                            {template.description}
                          </Typography>
                        }
                        secondary={
                          <>
                            <Typography
                              sx={{
                                color: "text.primary",
                                overflow: "hidden",
                                whiteSpace: "nowrap",
                                textOverflow: "ellipsis",
                              }}
                            >
                              {getFormattedValue(fieldKey, template.value)}
                            </Typography>
                          </>
                        }
                      />
                    </ListItem>
                    <Divider component="li"/>
                  </>
                ))}
              </List>
            </Grid2>
            <Grid2 size={7} sx={{height: "100%"}}>
              <Card
                sx={{
                  width: "100%",
                  height: "98%"
                }}
              >
                <CardContent sx={{
                  height: "100%",
                  overflowY: "auto",
                  "&::-webkit-scrollbar": {
                    width: "4px"
                  },
                  "&::-webkit-scrollbar-track": {
                    background: "transparent"
                  },
                  "&::-webkit-scrollbar-thumb": {
                    background: "rgba(0, 0, 0, 0.1)",
                    borderRadius: "2px",
                    "&:hover": {
                      background: "rgba(0, 0, 0, 0.2)"
                    }
                  },
                  scrollbarWidth: "thin",
                  scrollbarColor: "rgba(0, 0, 0, 0.1) transparent"
                }}>
                  <Typography sx={{whiteSpace: "pre-wrap"}}>
                    {getFormattedValue(fieldKey, selectedTemplate?.value)}
                  </Typography>
                </CardContent>
              </Card>
            </Grid2>
          </Grid2>
        </Box>
      </DialogContent>
      <Box sx={{
        p: 2,
        bgcolor: "background.paper",
        borderTop: 1,
        borderColor: "divider",
        position: "sticky",
        bottom: 0,
        zIndex: 1
      }}>
        <Stack direction="row" justifyContent="space-between" spacing={1}>
          <Button
            startIcon={<Plus/>}
            color="primary"
            variant={"text"}
            disableElevation
            disabled={editType !== ZyusetuEditType.Edit}
            onClick={() => setOpenCreateDialog(true)}
            sx={{fontWeight: "bold"}}
          >
            追加する
          </Button>
          <Stack direction="row" spacing={1}>
            <Button
              color="primary"
              variant={"text"}
              disableElevation
              disabled={!selectedTemplate || editType !== ZyusetuEditType.Edit}
              onClick={async () => {
                await onDelete(selectedTemplate!.id!)
              }}
            >
              削除
            </Button>
            <Button
              color="primary"
              variant={"text"}
              disableElevation
              disabled={!selectedTemplate || editType !== ZyusetuEditType.Edit}
              onClick={() => setEditTemplate(selectedTemplate)}
            >
              編集
            </Button>
            <Button
              color="primary"
              variant={"contained"}
              disableElevation
              onClick={() => onSelect(selectedTemplate?.value, selectedTemplate?.description)}
            >
              選択
            </Button>
          </Stack>
        </Stack>
      </Box>
    </Dialog>
  );
}
